import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { AvailableScheduleDTO, RequestStudentData } from "../../models/EnrollmentHistoryDTO";

const base = (campusId: number) => `/admin/campus/${campusId}/enrollmentPrerequests`;

export interface PrerequestPersonDTO {
	id: number;
	phone: string;
	name: string | null;
	/** Todos los cursos y horarios elegidos: varios o ninguno (Esteban, 2026-10-05). Los campos sueltos son el primero. */
	schedules: PrerequestScheduleDTO[];
	scheduleId: number | null;
	courseName: string;
	levelName: string;
	scheduleName: string;
	sessions: string;
	requestId: number | null;
	requestStatusId: number | null;
	requestStatus: string | null;
	/** Dónde va el cliente: solicitud, matricula, rechazada, revision, pago, matriculado, descartada o error. */
	stage?: PrerequestStage;
	/** Formulario del cliente por WhatsApp (2026-10-19). url solo para quien lo puede mandar. */
	form?: PrerequestFormDTO;
	/** Otras pre-solicitudes del plantel con el mismo teléfono (30 días antes o después). */
	samePhone: number[];
	/** Datos del alumno que mandó la asesora (opcionales). */
	studentData: RequestStudentData;
	/** Identificación del cliente (frente, reverso). */
	documents: PrerequestDocumentDTO[];
	/** Trae todo lo que la solicitud pide para enviarse, más la identificación. */
	complete: boolean;
}

export interface PrerequestScheduleDTO { scheduleId: number; courseName: string; levelName: string; scheduleName: string; periodName: string; sessions: string; }

export interface PrerequestDocumentDTO { id: number; name: string; mimeType: string | null; size: number | null; typeId?: number; }

export interface PrerequestDTO {
	id: number;
	campusId: number;
	statusId: number;
	status: string;
	createdById: number;
	createdBy: string | null;
	createdAt: string | null;
	advisors: { userId: string; name: string; email?: string | null; role?: string | null }[];
	notes: string | null;
	discardReason: string | null;
	discardedBy: string | null;
	discardedAt: string | null;
	people: PrerequestPersonDTO[];
	documents: PrerequestDocumentDTO[];
	permissions?: PrerequestPermissions;
	/** Solo al crear: "ya tiene solicitud #N", mismo teléfono en otra pre-solicitud. */
	notices?: string[];
}

export interface PrerequestPermissions { canCreate: boolean; canManage: boolean; seesAll: boolean; }

export interface PrerequestListDTO {
	data: PrerequestDTO[];
	total: number;
	perPage: number;
	currentPage: number;
	lastPage: number;
	counts: { pending: number; completed: number; discarded: number; inProcess?: number; enrolled?: number };
	permissions: PrerequestPermissions;
}

export interface PhoneCheckDTO { prerequestId: number; status: string; createdAt: string; createdBy: string; }

export const PRE_STATUS = { PENDIENTE: 1, COMPLETADA: 2, DESCARTADA: 3 };
export interface PrerequestFormDTO {
	status: "enviado" | "abierto" | "completado" | "ventana_cerrada" | "sin_chat" | "error" | "apagado" | null;
	error: string | null; sentAt: string | null; openedAt: string | null; completedAt: string | null; url: string | null;
	/** Lo que le falta al cliente, en palabras ("la fecha de nacimiento", "la foto de la identificación del tutor"…). */
	falta?: string[];
}
export type PrerequestStage = "solicitud" | "matricula" | "rechazada" | "revision" | "pago" | "matriculado" | "descartada" | "error";
/** Bandejas de Matrículas → Solicitudes en Proceso (2026-10-19); los números viejos siguen valiendo. */
export type PrerequestBucket = "proceso" | "matriculadas" | 3;

/** Pre-solicitudes de matrícula: la venta de la asesora antes de tener los datos del alumno. */
export class EnrollmentPrerequestsService {
	list = async (campusId: number, statusId: number | PrerequestBucket, search: string, perPage: number, page: number): Promise<AxiosResponse<PrerequestListDTO>> =>
		axiosInstance.get<PrerequestListDTO>(`${base(campusId)}?statusId=${statusId}&perPage=${perPage}&page=${page}&search=${encodeURIComponent(search || "")}`);

	details = async (campusId: number, id: number): Promise<AxiosResponse<PrerequestDTO>> =>
		axiosInstance.get<PrerequestDTO>(`${base(campusId)}/${id}/_details`);

	schedules = async (campusId: number): Promise<AxiosResponse<AvailableScheduleDTO[]>> =>
		axiosInstance.get<AvailableScheduleDTO[]>(`${base(campusId)}/_schedules`);

	phoneCheck = async (campusId: number, phone: string): Promise<AxiosResponse<PhoneCheckDTO[]>> =>
		axiosInstance.get<PhoneCheckDTO[]>(`${base(campusId)}/_phoneCheck?phone=${encodeURIComponent(phone)}`);

	/** ids[i] = identificación del cliente i (opcional, frente y reverso). */
	create = async (campusId: number, people: { phone: string; name: string; scheduleIds: number[]; studentData?: RequestStudentData | null }[], notes: string, files: File[],
		ids: File[][] = []): Promise<AxiosResponse<PrerequestDTO>> => {
		const form = new FormData();
		form.append("people", JSON.stringify(people));
		form.append("notes", notes || "");
		files.forEach((f) => form.append("files[]", f, f.name));
		ids.forEach((lista, i) => (lista || []).forEach((f) => form.append(`ids[${i}][]`, f, f.name)));
		return axiosInstance.post<PrerequestDTO>(base(campusId), form, { headers: { "Content-Type": "multipart/form-data" } });
	};

	/** Manda (o vuelve a mandar) el formulario de datos al cliente por WhatsApp. */
	enviarFormulario = async (campusId: number, id: number, personId: number): Promise<AxiosResponse<PrerequestDTO>> =>
		axiosInstance.post<PrerequestDTO>(`${base(campusId)}/${id}/_people/${personId}/_formulario`);

	discard = async (campusId: number, id: number, reason: string): Promise<AxiosResponse<PrerequestDTO>> =>
		axiosInstance.post<PrerequestDTO>(`${base(campusId)}/${id}/_discard`, { reason });

	downloadDocument = async (campusId: number, id: number, documentId: number) =>
		axiosInstance.get<Blob>(`${base(campusId)}/${id}/_documents/${documentId}`, { responseType: "blob" });
}
